import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cancelGenerationJob } from '../utils/cancelJob';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../i18n';


// How long a completed job's card stays up offering View/Dismiss before
// it auto-dismisses into an unread dashboard notification.
const COMPLETED_LINGER_MS = 60 * 1000;

// Draggable pill: the chosen corner persists for the session so the
// pill stays where the user put it across pages.
const CORNER_KEY = 'au_pill_corner';
const EDGE_GAP = 20;
const DRAG_THRESHOLD = 8;

/**
 * Global floating progress indicator — shown on every page while any
 * background job is running. Polls /api/jobs/active every 3s; clicking
 * a running job card navigates to the page that will show its result.
 *
 * When a job COMPLETES its card stays for 60 seconds in a "Completed"
 * state with View + Dismiss:
 *   - View    → navigate to the result, remove the pill, and mark the
 *               completion notification READ (kept off the dashboard).
 *   - Dismiss → remove the pill; the completion notification stays
 *               UNREAD and surfaces on the dashboard.
 *   - Neither within 60s → auto-removes, same as Dismiss.
 * Multiple completed cards stack, each with its own timer.
 */
export default function JobProgressPill({ shop }) {
  const [jobs, setJobs] = useState([]);
  const [tick, setTick] = useState(0);
  // Which running job's ✕ was pressed — its card shows a quick inline
  // "Cancel this generation?" confirm instead of cancelling instantly.
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  // Jobs already Viewed/Dismissed locally — hidden immediately, even
  // before the backend flag lands on the next poll.
  const dismissedRef = useRef(new Set());
  // Statuses seen on the previous poll — a running→complete transition
  // means a fresh completion notification exists server-side, so the
  // bell (useNotifications) is told to refetch immediately instead of
  // waiting for its slow poll.
  const prevStatusRef = useRef({});
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!shop) return undefined;
    let active = true;
    // Resolves to whether any job is still running (drives the cadence).
    const poll = async () => {
      try {
        const res = await apiFetch(
          `/api/jobs/active?includeRecent=1`,
          { credentials: 'include' },
        );
        const data = await res.json();
        if (!active) return false;
        const list = (data.jobs || []).filter(j => !dismissedRef.current.has(j.id));
        const justCompleted = list.some(j =>
          j.status === 'complete' && prevStatusRef.current[j.id] === 'running');
        prevStatusRef.current = Object.fromEntries(list.map(j => [j.id, j.status]));
        setJobs(list);
        if (justCompleted) {
          window.dispatchEvent(new Event('autonoma:refresh-notifications'));
        }
        // Queued builds (multi-product submits past the 4-at-once cap)
        // start on their own — keep the live cadence for them too.
        return list.some(j => j.status === 'running' || j.status === 'queued');
      } catch {
        return true; // transient network error — keep the fast cadence, next poll retries
      }
    };
    // Adaptive cadence: 3s while anything is running (progress feels
    // live), 15s once idle, and a slow 60s heartbeat while the admin tab
    // is hidden — previously this fired every 3s for the lifetime of the
    // session on every page, jobs or not. Pages that start a job dispatch
    // 'autonoma:job-started' so the pill appears immediately.
    let timer = null;
    let inFlight = false;
    const run = async () => {
      if (!active || inFlight) return;
      inFlight = true;
      const running = await poll();
      inFlight = false;
      if (!active) return;
      if (timer) clearTimeout(timer);
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      timer = setTimeout(run, hidden ? 60000 : (running ? 3000 : 15000));
    };
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    const onJobStarted = () => run();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('autonoma:job-started', onJobStarted);
    run();
    // A 1s tick keeps the countdown label fresh and fires auto-dismiss.
    const ticker = setInterval(() => setTick(n => n + 1), 1000);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
      clearInterval(ticker);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('autonoma:job-started', onJobStarted);
    };
  }, [shop]);

  const routeFor = (job) => {
    // A RUNNING ads job lives in the Ads "In Progress" tab (there's
    // nothing to open in the wizard yet) — but a COMPLETED one opens
    // straight into the Step 2 creatives preview via ?jobId= (no
    // In Progress + Continue detour).
    if (job.type === 'ads') {
      return job.status === 'complete' ? `/ads?jobId=${job.id}` : '/ads?tab=progress';
    }
    if (job.type === 'advertorial') return `/advertorials?jobId=${job.id}`;
    if (job.type === 'landing_page') return `/products/create-page?jobId=${job.id}`;
    if (job.type === 'website') return `/website?jobId=${job.id}`;
    if (job.type === 'images') return `/products/create-images?jobId=${job.id}`;
    if (job.type === 'product_build') return `/new-product?jobId=${job.id}`;
    if (job.type === 'split_test_build') return `/split-testing?jobId=${job.id}`;
    if (job.type === 'split_test_setup') return `/split-testing?jobId=${job.id}`;
    if (job.type === 'upsell_discovery') return `/upsell?jobId=${job.id}`;
    if (job.type === 'upsell_build') return `/upsell?jobId=${job.id}`;
    if (job.type === 'product_finder') return `/find-products?jobId=${job.id}`;
    // Own-ad edits open straight into the creative review step.
    if (job.type === 'edit_own_ad') return `/ads?jobId=${job.id}`;
    // Ad copy opens the Step 3 result.
    if (job.type === 'ad_copy') return `/ads?jobId=${job.id}`;
    // Publishing: a COMPLETED publish's View opens the live campaign in
    // Meta Ads Manager (deep-linked external URL from job.result, new
    // tab via viewJob) — the in-app ?jobId= publish screen is the
    // fallback. While still publishing, clicking goes to Ads.
    if (job.type === 'publish_ads') {
      if (job.status !== 'complete') return '/ads';
      return job.result?.adsManagerUrl || `/ads?jobId=${job.id}`;
    }
    // Cart upgrades publish directly — View opens the STOREFRONT with
    // the cart in view (password/draft-safe URL resolved by the
    // backend); Website page as fallback.
    if (job.type === 'cart_upgrade') return job.meta?.cartViewUrl || job.result?.cartViewUrl || '/website';
    // Shipping/markets changes live in Shopify admin — a COMPLETED
    // job's View opens the matching settings page (external URL, new
    // tab via viewJob); while running, clicking stays in-app.
    if (job.type === 'shipping_setup' || job.type === 'markets_update') {
      if (job.status !== 'complete') return '/';
      const handle = String(shop || '').replace('.myshopify.com', '');
      const page = job.type === 'markets_update' ? 'markets' : 'shipping';
      return handle ? `https://admin.shopify.com/store/${handle}/settings/${page}` : '/';
    }
    // Preview refines carry the route that reopens the exact preview
    // they came from (set by the backend at job creation).
    if (job.type === 'refine') return job.meta?.returnRoute || '/';
    return '/';
  };

  const labelFor = (job) => {
    // Refines describe the change: "Updating your advertorial", etc.
    if (job.type === 'refine') return t('ui.jobs.updating', { what: job.meta?.contentType || 'content' });
    const key = `ui.jobs.${job.type}`;
    const label = t(key);
    return label === key ? t('ui.jobs.working') : label;
  };

  const removeLocally = (jobId) => {
    dismissedRef.current.add(jobId);
    setJobs(prev => prev.filter(j => j.id !== jobId));
  };

  // Dismiss / timeout: pill goes away, the completion notification
  // stays unread and shows on the dashboard.
  const dismissJob = (job) => {
    removeLocally(job.id);
    apiFetch(`/api/jobs/${job.id}/dismiss`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
  };

  // View: go to the result, mark its notification read (off dashboard).
  // External http(s) targets (e.g. the storefront cart) open in a new
  // tab; everything else navigates in-app.
  const viewJob = (job) => {
    removeLocally(job.id);
    apiFetch(`/api/jobs/${job.id}/seen`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {});
    const target = routeFor(job);
    if (/^https?:\/\//i.test(target)) window.open(target, '_blank', 'noopener');
    else navigate(target);
  };

  // Cancel a RUNNING job: the backend marks it 'cancelled' (processors
  // abort between steps, partial output is discarded, the slot frees
  // immediately, no completion notification) and the pill goes away.
  const cancelJob = (job) => {
    setConfirmCancelId(null);
    removeLocally(job.id);
    cancelGenerationJob(shop, job.id);
  };

  const secondsLeft = (job) => {
    const elapsed = Date.now() - new Date(job.updatedAt).getTime();
    return Math.max(0, Math.ceil((COMPLETED_LINGER_MS - elapsed) / 1000));
  };

  // Fire auto-dismiss for completed cards whose 60s ran out. Driven by
  // the 1s tick so it can't set state mid-render.
  useEffect(() => {
    jobs.forEach(job => {
      if (job.status === 'complete' && secondsLeft(job) === 0 && !dismissedRef.current.has(job.id)) {
        dismissJob(job);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, jobs]);

  const visible = jobs.filter(j => !dismissedRef.current.has(j.id));

  // ── Draggable container: drag anywhere, snap to the nearest corner ──
  // The whole stack moves as a group. Position is done purely with a
  // transform from the viewport's top-left, so the snap animates
  // smoothly regardless of which corner it lands in. The chosen corner
  // is remembered for the session (sessionStorage), default bottom-right.
  const containerRef = useRef(null);
  const [corner, setCorner] = useState(() => {
    try { return sessionStorage.getItem(CORNER_KEY) || 'br'; } catch { return 'br'; }
  });
  const [basePos, setBasePos] = useState(null); // snapped {x, y}
  const [dragOffset, setDragOffset] = useState(null); // {dx, dy} while dragging
  const dragRef = useRef(null); // { startX, startY, moved }
  const suppressClickRef = useRef(false);

  const posForCorner = useCallback((c) => {
    const el = containerRef.current;
    if (!el) return null;
    return {
      x: c.includes('l') ? EDGE_GAP : window.innerWidth - el.offsetWidth - EDGE_GAP,
      y: c.includes('t') ? EDGE_GAP : window.innerHeight - el.offsetHeight - EDGE_GAP,
    };
  }, []);

  const computeBasePos = useCallback(() => {
    const next = posForCorner(corner);
    if (next) setBasePos(next);
  }, [corner, posForCorner]);

  // Re-anchor whenever the stack's size can have changed (cards come and
  // go, messages rewrap) and on viewport resize.
  useLayoutEffect(() => { computeBasePos(); }, [computeBasePos, jobs]);
  useEffect(() => {
    window.addEventListener('resize', computeBasePos);
    return () => window.removeEventListener('resize', computeBasePos);
  }, [computeBasePos]);

  const onPointerDown = (e) => {
    if (typeof e.button === 'number' && e.button !== 0) return;
    // Buttons (View/Dismiss) are never drag handles — let them take the
    // press and fire their own click untouched. Drag starts from the
    // pill BODY only.
    if (e.target.closest?.('button')) return;
    // Record the press but DON'T capture the pointer yet: capturing on
    // pointerdown retargets the follow-up mouseup/click to the container,
    // which is exactly what broke clicking the cards. Capture only once
    // real movement makes this a drag.
    dragRef.current = { startX: e.clientX, startY: e.clientY, moved: false, pointerId: e.pointerId };
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    // Below the threshold it's a click (open the card) — not a drag.
    if (!d.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      d.moved = true;
      // Now it IS a drag: capture so the move keeps tracking outside the
      // container, and so the trailing click lands here to be swallowed.
      try { containerRef.current?.setPointerCapture(d.pointerId); } catch { /* older browsers */ }
    }
    setDragOffset({ dx, dy });
  };

  const onPointerUp = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d?.moved) return;
    suppressClickRef.current = true; // swallow the click this drag would fire
    // Snap to whichever corner the stack's centre is nearest.
    const el = containerRef.current;
    const rect = el?.getBoundingClientRect();
    const cx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const cy = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
    const next = `${cy < window.innerHeight / 2 ? 't' : 'b'}${cx < window.innerWidth / 2 ? 'l' : 'r'}`;
    // Set the new base in the same commit as clearing the drag offset so
    // the transform transition animates straight into the corner.
    const snapped = posForCorner(next);
    setDragOffset(null);
    setCorner(next);
    if (snapped) setBasePos(snapped);
    try { sessionStorage.setItem(CORNER_KEY, next); } catch { /* private mode */ }
  };

  // After a real drag, the browser still fires a click on whatever card
  // the pointer ended over — swallow exactly that one.
  const onClickCapture = (e) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  if (visible.length === 0) return null;

  const gradient = 'linear-gradient(135deg, #6B4EE8 0%, #4A90E2 100%)';

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClickCapture={onClickCapture}
      style={{
        position: 'fixed', top: 0, left: 0, zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: '10px',
        transform: basePos
          ? `translate3d(${basePos.x + (dragOffset?.dx || 0)}px, ${basePos.y + (dragOffset?.dy || 0)}px, 0)`
          : undefined,
        transition: dragOffset ? 'none' : 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
        visibility: basePos ? 'visible' : 'hidden',
        touchAction: 'none',
        cursor: dragOffset ? 'grabbing' : 'grab',
      }}
    >
      {visible.map(job => (job.status === 'complete' ? (
        <div
          key={job.id}
          style={{
            background: 'white', borderRadius: '14px', padding: '14px 18px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
            minWidth: '260px', maxWidth: '340px', border: '1px solid #e4e0f7',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <span
              aria-hidden="true"
              style={{
                width: '22px', height: '22px', borderRadius: '50%', background: gradient,
                color: 'white', display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', fontSize: '13px', fontWeight: '800', flexShrink: 0,
              }}
            >
              ✓
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: '12px', fontWeight: '800', letterSpacing: '0.04em',
                  textTransform: 'uppercase', background: gradient,
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}
              >
                Completed
              </div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a' }}>
                {labelFor(job)}{job.meta?.productName ? ` — ${job.meta.productName}` : ''}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => viewJob(job)}
              style={{
                flex: 1, background: gradient, color: 'white', border: 'none',
                borderRadius: '9px', padding: '8px 0', fontSize: '13px',
                fontWeight: '700', cursor: 'pointer',
              }}
            >
              View
            </button>
            <button
              type="button"
              onClick={() => dismissJob(job)}
              style={{
                flex: 1, background: 'white', color: '#555',
                border: '1px solid #ddd', borderRadius: '9px', padding: '8px 0',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
            <span style={{ fontSize: '11px', color: '#aaa', minWidth: '28px', textAlign: 'right' }}>
              {secondsLeft(job)}s
            </span>
          </div>
        </div>
      ) : (
        <div
          key={job.id}
          role="button"
          tabIndex={0}
          onClick={() => navigate(routeFor(job))}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(routeFor(job)); }}
          style={{
            background: 'white', borderRadius: '14px', padding: '14px 18px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.16)', cursor: 'pointer',
            minWidth: '260px', maxWidth: '340px', border: '1px solid #eee',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a' }}>
              {labelFor(job)}{job.meta?.productName ? ` — ${job.meta.productName}` : ''}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              <span
                style={{
                  fontSize: '12px', fontWeight: '700',
                  background: gradient,
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}
              >
                {job.status === 'queued' ? 'Queued' : `${job.progress}%`}
              </span>
              {job.type !== 'publish_ads' && (
              <button
                type="button"
                aria-label="Cancel this generation"
                title="Cancel this generation"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmCancelId(prev => (prev === job.id ? null : job.id));
                }}
                style={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  border: '1px solid #e0e0e0', background: 'white', color: '#999',
                  fontSize: '11px', lineHeight: 1, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  padding: 0, flexShrink: 0,
                }}
              >
                ✕
              </button>
              )}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${job.progress}%`, height: '100%',
                background: gradient,
                borderRadius: '4px', transition: 'width 0.5s ease',
              }}
            />
          </div>
          <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>{job.message}</div>
          {confirmCancelId === job.id && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f0f0f0',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#1a1a1a', flex: 1 }}>
                Cancel this generation?
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); cancelJob(job); }}
                style={{
                  background: '#d82c0d', color: 'white', border: 'none',
                  borderRadius: '8px', padding: '6px 10px', fontSize: '12px',
                  fontWeight: '700', cursor: 'pointer',
                }}
              >
                Yes, cancel
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setConfirmCancelId(null); }}
                style={{
                  background: 'white', color: '#555', border: '1px solid #ddd',
                  borderRadius: '8px', padding: '6px 10px', fontSize: '12px',
                  fontWeight: '600', cursor: 'pointer',
                }}
              >
                Keep going
              </button>
            </div>
          )}
        </div>
      )))}
    </div>
  );
}
